// The small slice of markdown the chat's answers use, split into pieces the
// panel renders as React nodes (never as HTML, so model output stays inert).

export type RichInline =
    | { kind: 'text'; text: string }
    | { kind: 'bold'; text: string }
    | { kind: 'code'; text: string }
    | { kind: 'link'; text: string; href: string };

export type RichBlock = { kind: 'lines'; lines: RichInline[][] } | { kind: 'code'; language: string; code: string };

// An unclosed fence runs to the end: while an answer is still streaming in,
// its code block is shown as code from the first line, not as raw backticks.
const FENCE = /```[ \t]*([\w#+.-]*)[^\n]*\n?([\s\S]*?)(?:\n?```|$)/g;
const INLINE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|`([^`\n]+)`/g;
const BULLET = /^(\s*)[*-]\s+/;
const HEADING = /^#{1,6}\s+(.*)$/;

export const parseInline = (line: string): RichInline[] => {
    const pieces: RichInline[] = [];
    let last = 0;
    INLINE.lastIndex = 0;
    let match = INLINE.exec(line);
    while (match !== null) {
        if (match.index > last) {
            pieces.push({ kind: 'text', text: line.slice(last, match.index) });
        }
        if (match[1] && match[2]) {
            pieces.push({ kind: 'link', text: match[1], href: match[2] });
        } else if (match[3]) {
            pieces.push({ kind: 'bold', text: match[3] });
        } else if (match[4]) {
            pieces.push({ kind: 'code', text: match[4] });
        }
        last = match.index + match[0].length;
        match = INLINE.exec(line);
    }
    if (last < line.length) {
        pieces.push({ kind: 'text', text: line.slice(last) });
    }
    return pieces;
};

// "* item" / "- item" become "• item"; "### Title" becomes a bold line.
const parseLine = (line: string): RichInline[] => {
    const heading = HEADING.exec(line);
    if (heading) {
        return [{ kind: 'bold', text: heading[1] }];
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
        return [{ kind: 'text', text: `${bullet[1]}• ` }, ...parseInline(line.slice(bullet[0].length))];
    }
    return parseInline(line);
};

const pushLines = (blocks: RichBlock[], text: string): void => {
    if (!text) {
        return;
    }
    blocks.push({ kind: 'lines', lines: text.split('\n').map(parseLine) });
};

export const parseRichText = (text: string): RichBlock[] => {
    const blocks: RichBlock[] = [];
    let last = 0;
    FENCE.lastIndex = 0;
    let match = FENCE.exec(text);
    while (match !== null) {
        // A fence's own surrounding newlines belong to the code block's frame.
        pushLines(blocks, text.slice(last, match.index).replace(/\n$/, ''));
        blocks.push({ kind: 'code', language: match[1], code: match[2] });
        last = match.index + match[0].length;
        if (text[last] === '\n') {
            last += 1;
        }
        if (match[0].length === 0) {
            break;
        }
        match = FENCE.exec(text);
    }
    pushLines(blocks, text.slice(last));
    return blocks;
};
